import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Church, ShoppingBag, Users, Building2, Shield, Lock, Menu, X, Search, MapPin, Store, Newspaper } from "lucide-react";
import { getLiturgicalSeason, getSeasonCSSVars, getSeasonLogo } from "@/lib/liturgicalCalendar";
import { useSEO } from "@/hooks/useSEO";

export default function LandingPage() {
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [season, setSeason] = useState(() => getLiturgicalSeason());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Auto-redirect authenticated users to dashboard
  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token") || (() => {
      const m = document.cookie.match(/(?:^|; )parish_token=([^;]*)/);
      return m ? m[1] : null;
    })();
    if (token) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);
  
  // SEO
  useSEO({
    title: "Catholic Community App",
    description: "Connect with your parish community, find Mass times, shop the Catholic marketplace, and discover Catholic-owned businesses. Join the trusted platform for Catholic families.",
    path: "/"
  });
  
  useEffect(() => {
    const currentSeason = getLiturgicalSeason();
    setSeason(currentSeason);
    // Apply CSS variables
    const root = document.documentElement;
    const vars = getSeasonCSSVars(currentSeason);
    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, []);

  const publicNavItems = [
    { path: "/sacrament-finder", label: "Sacrament Finder", icon: Search },
    { path: "/marketplace", label: "Marketplace", icon: Store },
    { path: "/directory", label: "Business Directory", icon: MapPin },
    { path: "/vatican-news", label: "Vatican News", icon: Newspaper },
  ];

  const features = [
    { icon: Church, title: "Parish Community", desc: "Connect with your parish family. Share prayers, updates, and celebrate together." },
    { icon: ShoppingBag, title: "Faithful Marketplace", desc: "Buy and sell religious items, handmade crafts, and services within your community." },
    { icon: Users, title: "Verified Members", desc: "Parish administrators verify every member, ensuring a trusted community." },
    { icon: Building2, title: "Catholic Business Directory", desc: "Discover and support Catholic-owned businesses in your area. Find services you can trust." },
    { icon: Shield, title: "Family-Safe Content", desc: "A community moderated for Catholic values. No inappropriate ads or harmful content." },
    { icon: Lock, title: "Your Data Stays Yours", desc: "We never sell your data to third parties. Your privacy is sacred to us." },
  ];

  const primaryColor = season.colors.primary;
  const primaryHover = season.colors.primaryHover;
  const secondaryColor = season.colors.secondary;

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      
      {/* Season Indicator - Clickable link to liturgical calendar */}
      <div 
        className="text-center py-2 text-sm font-['Nunito'] text-white cursor-pointer hover:brightness-110 transition-all"
        style={{ backgroundColor: primaryColor }}
        data-testid="season-indicator"
        onClick={() => navigate("/liturgical-calendar")}
      >
        <span className="opacity-90">Current Season: <strong>{season.name}</strong></span>
        <span className="mx-2 opacity-50">|</span>
        <span className="opacity-75 italic">{season.description}</span>
        <span className="ml-2 opacity-60 text-xs">Learn more →</span>
      </div>

      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-[#E5E0D5] sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 md:px-12 lg:px-20 py-4">
          <div className="flex items-center gap-3">
            <img 
              src={getSeasonLogo(season)} 
              alt="Parish" 
              className="w-10 h-10 rounded-lg transition-all duration-500"
            />
            <span className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26] tracking-tight" data-testid="landing-logo">Parish</span>
          </div>
          
          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-1">
            {publicNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="px-3 py-2 rounded-md text-sm font-medium font-['Nunito'] text-[#6B665E] hover:text-[#2D2A26] hover:bg-[#F9F7F2] transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
          
          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-login-btn" className="text-[#2D2A26] hover:bg-[#F0EBE0] font-['Nunito']">
              Sign In
            </Button>
            <Button 
              onClick={() => navigate("/register")} 
              data-testid="landing-register-btn" 
              className="text-white font-['Nunito'] rounded-md px-6 transition-colors duration-300"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={(e) => e.target.style.backgroundColor = primaryHover}
              onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
            >
              Join Your Parish
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-md text-[#6B665E] hover:bg-[#F9F7F2]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E5E0D5] bg-white px-4 py-3 space-y-1">
            {publicNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium font-['Nunito'] text-[#6B665E] hover:bg-[#F9F7F2]"
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-[#E5E0D5] mt-3 space-y-2">
              <Button variant="outline" className="w-full font-['Nunito']" onClick={() => navigate("/login")}>
                Sign In
              </Button>
              <Button 
                className="w-full text-white font-['Nunito']"
                style={{ backgroundColor: primaryColor }}
                onClick={() => navigate("/register")}
              >
                Join Your Parish
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative px-6 md:px-12 lg:px-20 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="animate-fade-in">
            <h1 className="font-['Crimson_Text'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D2A26] tracking-tight leading-[1.1] mb-6" data-testid="hero-title">
              Social Media for the Catholic Faithful
            </h1>
            <p className="text-base md:text-lg text-[#6B665E] font-['Nunito'] leading-relaxed mb-10 max-w-lg">
              Join a trusted community of Catholic faithful. Share your parish life, discover handmade religious treasures, and grow together in faith.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => navigate("/register")} 
                data-testid="hero-cta-join" 
                className="text-white font-['Nunito'] font-semibold rounded-md px-8 h-12 text-base shadow-md active:scale-95 transition-all duration-300"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => e.target.style.backgroundColor = primaryHover}
                onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
              >
                Join Your Parish
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate("/login")} 
                data-testid="hero-cta-signin" 
                className="font-['Nunito'] font-semibold rounded-md px-8 h-12 text-base border-2 transition-colors duration-300"
                style={{ borderColor: primaryColor, color: primaryColor }}
              >
                Sign In
              </Button>
            </div>
          </div>
          <div className="animate-fade-in-delay-2 relative">
            <div className="rounded-lg overflow-hidden shadow-xl border border-[#E5E0D5]">
              <img
                src="https://customer-assets.emergentagent.com/job_187be98d-615b-4841-9610-d20901a32c5d/artifacts/a2rfh9br_392f3d27454c6086d6113140fc112dad6455db8740fddcd1a610379bf1fdaf25.png"
                alt="Catholic families gathering after Mass with children playing"
                className={`w-full h-[360px] md:h-[440px] object-cover transition-opacity duration-700 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImgLoaded(true)}
              />
              {!imgLoaded && <div className="absolute inset-0 bg-[#F0EBE0] animate-pulse rounded-lg" />}
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white border border-[#E5E0D5] rounded-md px-4 py-3 shadow-lg">
              <p className="text-sm font-['Nunito'] font-semibold text-[#2D2A26]">3,200+ Members</p>
              <p className="text-xs text-[#6B665E]">Across 50+ parishes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 md:px-12 lg:px-20 py-20 bg-white border-t border-b border-[#E5E0D5]">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-['Crimson_Text'] text-4xl md:text-5xl font-semibold text-[#2D2A26] tracking-tight mb-16">Built to Grow Real-World Catholic Communities</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div 
                key={i} 
                className={`group p-8 bg-[#F9F7F2] border border-[#E5E0D5] rounded-md hover:shadow-lg transition-all duration-300 animate-fade-in-delay-${Math.min(i + 1, 3)}`} 
                data-testid={`feature-card-${i}`}
              >
                <div 
                  className="w-12 h-12 rounded-sm flex items-center justify-center mb-5 transition-colors duration-300"
                  style={{ backgroundColor: season.colors.muted }}
                >
                  <f.icon className="w-6 h-6 transition-colors duration-300" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26] mb-2">{f.title}</h3>
                <p className="text-sm text-[#6B665E] font-['Nunito'] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 lg:px-20 py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-['Crimson_Text'] text-4xl md:text-5xl font-semibold text-[#2D2A26] tracking-tight mb-6">Ready to Connect?</h2>
          <p className="text-base md:text-lg text-[#6B665E] font-['Nunito'] leading-relaxed mb-10">
            Join thousands of Catholics already building stronger parish communities through Parish.
          </p>
          <Button 
            onClick={() => navigate("/register")} 
            data-testid="cta-join-btn" 
            className="text-white font-['Nunito'] font-semibold rounded-md px-10 h-12 text-base shadow-md active:scale-95 transition-all duration-300"
            style={{ backgroundColor: secondaryColor }}
          >
            Join Your Parish Today
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-20 py-10 bg-[#2D2A26] text-white/60">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
            <div className="flex items-center gap-2">
              <img 
                src={getSeasonLogo(season)} 
                alt="Parish" 
                className="w-7 h-7 rounded-md"
              />
              <span className="font-['Crimson_Text'] text-lg font-semibold text-white">Parish</span>
            </div>
            <p className="text-sm font-['Nunito']">Connecting the faithful, one parish at a time.</p>
          </div>
          
          {/* Legal Links */}
          <div className="border-t border-white/10 pt-6">
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs font-['Nunito']">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="/cookies" className="hover:text-white transition-colors">Cookie Policy</a>
              <a href="/community-guidelines" className="hover:text-white transition-colors">Community Guidelines</a>
              <a href="/marketplace-terms" className="hover:text-white transition-colors">Marketplace Terms</a>
            </div>
            <p className="text-center text-xs mt-4 text-white/40 font-['Nunito']">
              © {new Date().getFullYear()} Parish. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
